// Blog-Radar tab (§2): content backlog per portal — thin pillars to expand,
// stale articles, cannibalisation suspects to merge, courses without a spoke.
// Read-only view over om_blog_reports (BLOG_RADAR_KONTRAKT_FRANK.md).
import type { MarketingDeps } from "./marketing.js";
import { wireMotorRun } from "./marketing-poll.js";

interface ThinItem { id: number; words: number; hits: number; title: string; url: string }
interface StaleItem { id: number; mod: string; hits: number; words: number; title: string; url: string }
interface CannibalItem {
  sim: number; shared: string[];
  a: { id: number; title: string; hits: number };
  b: { id: number; title: string; hits: number };
}
interface RadarPortal {
  label: string; slug: string; ratgeber: number;
  counts: { thin: number; stale: number; cannibal: number; courseGaps: number; coursesTotal: number };
  thin: ThinItem[]; stale: StaleItem[]; cannibal: CannibalItem[]; courseGaps: string[];
  noCourses?: boolean;   // Portal ohne Kurs-Katalog (z.B. Privatpraxis FASZ) → Lane D ausblenden
}
interface RadarSummary { portals: RadarPortal[] }
interface ReportsResponse {
  latest: { id: number; runStamp: string; createdAt: string; status: string; summary: RadarSummary } | null;
  history: Array<{ id: number; runStamp: string; status: string }>;
}

function lane(host: HTMLElement, title: string, count: number, render: (body: HTMLElement) => void): void {
  const d = document.createElement("details");
  d.className = "om-lane";
  const sum = document.createElement("summary");
  sum.textContent = `${title} (${count})`;
  d.appendChild(sum);
  const body = document.createElement("div");
  body.className = "om-lane-body";
  if (count) render(body);
  else body.innerHTML = '<p class="dim">—</p>';
  d.appendChild(body);
  host.appendChild(d);
}

function linkList(items: Array<{ url: string; label: string }>): HTMLElement {
  const ul = document.createElement("ul");
  ul.className = "om-link-list";
  for (const it of items) {
    const li = document.createElement("li");
    if (it.url) {
      const a = document.createElement("a");
      a.href = it.url; a.target = "_blank"; a.rel = "noopener";
      a.textContent = it.label;
      li.appendChild(a);
    } else {
      li.textContent = it.label;
    }
    ul.appendChild(li);
  }
  return ul;
}

function renderPortal(host: HTMLElement, p: RadarPortal): void {
  const block = document.createElement("div");
  block.className = "om-portal";

  const head = document.createElement("div");
  head.className = "om-portal-head";
  const h = document.createElement("h3");
  h.textContent = `${p.label} · ${p.ratgeber} Ratgeber`;
  head.appendChild(h);
  const badges = document.createElement("div");
  badges.className = "om-badges";
  const badge = (label: string, n: number, kind: string): void => {
    const b = document.createElement("span");
    b.className = `om-badge ${n ? kind : "ok"}`;
    b.textContent = `${label}: ${n}`;
    badges.appendChild(b);
  };
  badge("Dünn", p.counts.thin, "warn");
  badge("Veraltet", p.counts.stale, "warn");
  badge("Kannibal.", p.counts.cannibal, "bad");
  if (!p.noCourses) badge("Kurslücken", p.counts.courseGaps, "warn");
  head.appendChild(badges);
  block.appendChild(head);

  lane(block, "A · Dünne Pillars (ausbauen)", p.counts.thin, (b) => {
    if (p.thin.length < p.counts.thin) {
      const hint = document.createElement("p");
      hint.className = "om-lane-hint";
      hint.textContent = `Top ${p.thin.length} von ${p.counts.thin} (nach Hits)`;
      b.appendChild(hint);
    }
    b.appendChild(linkList(p.thin.map((t) => ({ url: t.url, label: `#${t.id} · ${t.words} W · ${t.hits} Hits · ${t.title}` }))));
  });
  lane(block, "B · Veraltet (aktualisieren)", p.counts.stale, (b) => {
    b.appendChild(linkList(p.stale.map((s) => ({ url: s.url, label: `#${s.id} · Stand ${s.mod} · ${s.words} W · ${s.title}` }))));
  });
  lane(block, "C · Kannibalisierung (mergen)", p.counts.cannibal, (b) => {
    const ul = document.createElement("ul");
    ul.className = "om-link-list";
    for (const c of p.cannibal) {
      const li = document.createElement("li");
      li.textContent = `${c.sim}% ähnlich · #${c.a.id} „${c.a.title}" (${c.a.hits}) ↔ #${c.b.id} „${c.b.title}" (${c.b.hits})${c.shared?.length ? ` · gemeinsam: ${c.shared.join(", ")}` : ""}`;
      ul.appendChild(li);
    }
    b.appendChild(ul);
  });
  if (!p.noCourses) {
    lane(block, "D · Kurse ohne Spoke", p.counts.courseGaps, (b) => {
      b.appendChild(linkList(p.courseGaps.map((g) => ({ url: g.startsWith("http") ? g : "", label: g }))));
    });
  }

  host.appendChild(block);
}

export function initRadarTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-radar-summary]") as HTMLElement | null;
  const historySel = m.querySelector("[data-radar-history]") as HTMLSelectElement | null;
  const msg = m.querySelector('[data-msg="radar"]') as HTMLElement | null;
  if (!host || !historySel) return;

  const load = async (): Promise<void> => {
    const r = await deps.api<ReportsResponse>("/api/marketing/blog-reports");
    const data = r.data;
    if (!data) return;
    historySel.textContent = "";
    for (const h of data.history) {
      const o = document.createElement("option");
      o.value = String(h.id);
      o.textContent = `${h.runStamp} (${h.status})`;
      historySel.appendChild(o);
    }
    host.textContent = "";
    if (!data.latest) {
      host.innerHTML = '<p class="dim">Noch kein Blog-Radar-Report. „Jetzt prüfen" startet den Motor.</p>';
      return;
    }
    for (const p of data.latest.summary.portals) renderPortal(host, p);
  };

  m.querySelector('[data-action="open-radar-report"]')?.addEventListener("click", () => {
    const id = historySel.value;
    if (id) window.open(`/api/marketing/blog-reports/${id}/markdown`, "_blank");
  });

  wireMotorRun({
    deps, modal: m,
    btn: m.querySelector('[data-action="run-radar"]'),
    msg, jobType: "blog_radar",
    runUrl: "/api/marketing/motors/blog_radar/run",
    onDone: load,
  });

  void load();
}
